import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { APIErrorType } from '../../api';
import {
  ExamReportChartType,
  ExamReportRankingType,
  ExamReportType,
  fetchExamReports,
  fetchExamReportsChartData,
  fetchExamReportsRanking
} from '../../api/exam-report';

export type useExamReportsReturnType = UseQueryResult<ExamReportType[]>;

export function useExamReports(
  examId: number | undefined,
  options: Omit<
    UseQueryOptions<
      ExamReportType[],
      APIErrorType,
      ExamReportType[],
      ReturnType<typeof getExamReportsQueryKey>
    >,
    'queryKey | queryFn'
  > = {}
): useExamReportsReturnType {
  if (!examId) throw Error('ExamId must be defined');
  return useQuery<
    ExamReportType[],
    APIErrorType,
    ExamReportType[],
    ReturnType<typeof getExamReportsQueryKey>
  >(getExamReportsQueryKey(examId), fetchExamReports, {
    staleTime: 30 * 1000,
    retry: false,
    ...options
  });
}

export const getExamReportsQueryKey: (examId: number) => [string, { examId: number }] = examId => [
  'exam-reports',
  { examId }
];

export type useExamReportsRankingReturnType = UseQueryResult<ExamReportRankingType[]>;

export function useExamReportsRanking(
  examId: number | undefined,
  options: Omit<
    UseQueryOptions<
      ExamReportRankingType[],
      APIErrorType,
      ExamReportRankingType[],
      ReturnType<typeof getExamReportsRankingQueryKey>
    >,
    'queryKey | queryFn'
  > = {}
): useExamReportsRankingReturnType {
  if (!examId) throw Error('ExamId must be defined');
  return useQuery<
    ExamReportRankingType[],
    APIErrorType,
    ExamReportRankingType[],
    ReturnType<typeof getExamReportsRankingQueryKey>
  >(getExamReportsRankingQueryKey(examId), fetchExamReportsRanking, {
    staleTime: 30 * 1000,
    retry: false,
    ...options
  });
}

export const getExamReportsRankingQueryKey: (
  examId: number
) => [string, { examId: number }] = examId => ['exam-reports-ranking', { examId }];

export type useExamReportChartReturnType = UseQueryResult<ExamReportChartType[]>;

export function useExamReportsChartData(): useExamReportChartReturnType {
  return useQuery<ExamReportChartType[], APIErrorType>(
    'exam-reports-chart-data',
    fetchExamReportsChartData,
    {
      staleTime: 30 * 1000,
      retry: false
    }
  );
}
